import { emailFieldSchema, realDateFieldSchema } from 'utils/schema.util'
import {
  addIssueIfInvalid,
  hasNumbers,
  isCpf,
  isFilled,
  isValidName,
  validateFamiliarReferencesV2,
} from 'utils/validate.util'
import { z } from 'zod'
import { AddressV2Init, addressV2Schema } from './address.type'
import {
  ERROR_MESSAGE_CPF_INCORRECT,
  ERROR_MESSAGE_INVALID_NAME,
  ERROR_MESSAGE_NUMBERS_IN_STRING,
} from './error.type'
import { SystemMessages } from 'utils/enums/system-message.enum'

const cpfFieldSchema = z
  .string()
  .refine(isCpf, {
    message: ERROR_MESSAGE_CPF_INCORRECT,
  })
  .nullish()

export const personReferenceSchemaV2 = z.object({
  id: z.number().nullish(),
  nome: z
    .string()
    .refine((field) => !isValidName(field), {
      message: ERROR_MESSAGE_INVALID_NAME,
    })
    .nullish(),
  dataNascimento: realDateFieldSchema.nullish(),
  cpf: cpfFieldSchema,
  identidade: z.string().nullish(),
})

export const referenceTypeSchemaV2 = z.object({
  id: z.number().nullish(),
  descricao: z.string().nullish(),
})

export const familiarReferenceSchemaV2 = z.object({
  id: z.number().nullish(),
  pessoaReferencia: personReferenceSchemaV2,
  tipoReferencia: referenceTypeSchemaV2,
  descricaoProfissao: z
    .string()
    .refine((field) => !hasNumbers(field), {
      message: ERROR_MESSAGE_NUMBERS_IN_STRING,
    })
    .nullish(),
  descricaoRendaBeneficio: z.string().nullish(),
  telefone: z.string().nullish(),
  email: emailFieldSchema.nullish(),
  informacaoComplementar: z.string().nullish(),
  endereco: addressV2Schema.nullish(),
})

export type PersonReferenceSchemaV2Type = z.infer<typeof personReferenceSchemaV2>

export type ReferenceV2Type = z.infer<typeof referenceTypeSchemaV2>

export type FamiliarReferenceV2Type = z.infer<typeof familiarReferenceSchemaV2>

export const personReferenceV2Init: PersonReferenceSchemaV2Type = {
  id: null,
  nome: '',
  dataNascimento: '',
  cpf: '',
  identidade: '',
}

export const referenceTypeV2Init: ReferenceV2Type = {
  id: null,
  descricao: '',
}

export const familiarReferenceV2Init: FamiliarReferenceV2Type = {
  id: null,
  pessoaReferencia: personReferenceV2Init,
  tipoReferencia: referenceTypeV2Init,
  descricaoProfissao: '',
  descricaoRendaBeneficio: '',
  telefone: '',
  email: '',
  informacaoComplementar: '',
  endereco: AddressV2Init,
}

export const referenciaFamiliarArray = z
  .array(familiarReferenceSchemaV2)
  .superRefine((referencias, ctx) => {
    referencias.forEach((referencia, index) => {
      // Ignorar pessoaReferencia.nome e tipoReferencia.id na verificação
      const otherFieldsFilled = isFilled({
        ...referencia,
        tipoReferencia: { id: undefined },
        pessoaReferencia: { ...referencia.pessoaReferencia, nome: undefined },
      })

      const nomeIsEmpty = !isFilled(referencia.pessoaReferencia.nome)
      const tipoIdIsEmpty = !isFilled(referencia.tipoReferencia.id)

      if (nomeIsEmpty) {
        // Validação do campo pessoaReferencia.nome
        addIssueIfInvalid(
          ctx,
          referencias.length > 1,
          index,
          ['pessoaReferencia', 'nome'],
          SystemMessages.MESSAGE_002,
        )

        // Validação para outros campos preenchidos, mas pessoaReferencia.nome vazio
        addIssueIfInvalid(
          ctx,
          otherFieldsFilled,
          index,
          ['pessoaReferencia', 'nome'],
          SystemMessages.MESSAGE_002,
        )

        // Validação para outros campos preenchidos, mas pessoaReferencia.nome vazio
        addIssueIfInvalid(
          ctx,
          !tipoIdIsEmpty,
          index,
          ['pessoaReferencia', 'nome'],
          SystemMessages.MESSAGE_002,
        )
      }

      if (tipoIdIsEmpty) {
        // Validação do campo tipoReferencia.id
        addIssueIfInvalid(
          ctx,
          referencias.length > 1,
          index,
          ['tipoReferencia', 'id'],
          SystemMessages.MESSAGE_002,
        )

        // Validação para outros campos preenchidos, mas tipoReferencia.id vazio
        addIssueIfInvalid(
          ctx,
          otherFieldsFilled,
          index,
          ['tipoReferencia', 'id'],
          SystemMessages.MESSAGE_002,
        )

        // Validação para outros campos preenchidos, mas tipoReferencia.id vazio
        addIssueIfInvalid(
          ctx,
          !nomeIsEmpty,
          index,
          ['tipoReferencia', 'id'],
          SystemMessages.MESSAGE_002,
        )
      }
    })
  })
  .transform((fields) => {
    return validateFamiliarReferencesV2(fields)
  })
